import React, { useState } from 'react';
import { AddBudgetButton } from './AddBudgetButton';
import { ImportCSVButton } from './ImportCSVButton';
import {
  CategoryItem,
  DestinationItem,
  ResponsibleItem,
  Transaction,
  Budget,
} from '../types/budget';
import { useCreateBudget } from '../hooks/useBudgetQueries';
import { useNavigate } from 'react-router-dom';
import { BudgetSettingsDialog } from './BudgetSettingsDialog';

interface CreateBudgetActionsProps {
  showText?: boolean;
  className?: string;
  spacing?: string;
  onSuccess?: (budgetId: string) => void;
}

export const CreateBudgetActions: React.FC<CreateBudgetActionsProps> = ({
  showText = false,
  className = '',
  spacing = 'space-x-3',
  onSuccess,
}) => {
  const navigate = useNavigate();
  const createBudgetMutation = useCreateBudget();
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [createdBudget, setCreatedBudget] = useState<Budget & { id: number }>();
  const [pendingNavigation, setPendingNavigation] = useState(false);

  const handleBudgetCreated = (budgetId: number, budget: Budget) => {
    const newBudget = { ...budget, id: budgetId };
    setCreatedBudget(newBudget);
    setSettingsDialogOpen(true);
    // Set pending navigation based on whether we have an onSuccess callback
    setPendingNavigation(!onSuccess);
  };

  const handleImportCSV = (
    fileName: string,
    transactions: Transaction[],
    categories: CategoryItem[],
    responsibles: ResponsibleItem[],
    destinations: DestinationItem[]
  ) => {
    const budget = {
      name: fileName,
      date: new Date(),
      transactions,
      categories,
      responsibles,
      destinations,
    };

    createBudgetMutation.mutate(budget, {
      onSuccess: (budgetId) => handleBudgetCreated(budgetId, budget),
    });
  };

  const handleBudgetUpdate = (updatedBudget: Budget) => {
    setCreatedBudget(updatedBudget as Budget & { id: number });
  };

  const handleCloseSettings = () => {
    setSettingsDialogOpen(false);
    setCreatedBudget(undefined);

    // Handle navigation after settings dialog is closed
    if (createdBudget) {
      if (onSuccess) {
        onSuccess(String(createdBudget.id));
      } else if (pendingNavigation) {
        navigate('/budgets/current', { replace: true });
      }
    }
    setPendingNavigation(false);
  };

  return (
    <div className={`flex items-center ${spacing}`}>
      <AddBudgetButton
        showText={showText}
        className={className}
        onBudgetCreated={handleBudgetCreated}
      />
      <ImportCSVButton showText={showText} className={className} onImport={handleImportCSV} />
      {createdBudget && (
        <BudgetSettingsDialog
          budget={createdBudget}
          isOpen={settingsDialogOpen}
          onClose={handleCloseSettings}
          onUpdate={handleBudgetUpdate}
        />
      )}
    </div>
  );
};
